import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Login, ResendVerificationCode } from '@auth/store/actions/auth.actions';
import { selectLoginError, selectLoginLoading, selectUsername } from '@auth/store/selectors/login.selectors';
import { Store } from '@ngrx/store';
import { interval, map, Observable, Subject, Subscription, takeUntil, takeWhile } from 'rxjs';

@Component({
  selector: 'app-auth-two-factors-code',
  templateUrl: './auth-two-factors-code.component.html',
  styleUrls: ['./auth-two-factors-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AuthTwoFactorsCodeComponent implements OnInit, OnDestroy {
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<Error | null>;
  public verificationCode: string = '';
  public countDown!: Subscription;
  public counter = 1000 * 60 * 2; // 2 minutes in milliseconds

  private tick = 1000;
  private readonly userName$: Observable<string | null>;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.loading$ = this.store.select(selectLoginLoading);
    this.error$ = this.store.select(selectLoginError);
    this.userName$ = this.store.select(selectUsername);
  }

  ngOnInit(): void {
    this.startCountdown();
    this.userName$.pipe(takeUntil(this.destroyed$)).subscribe((username) => {
      if (!username) {
        this.router.navigate(['/']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.countDown) {
      this.countDown.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public login(verificationCode: string): void {
    this.store.dispatch(Login({ username: '', verificationCode }));
  }

  public resendCode(): void {
    this.counter = 1000 * 60 * 2;
    this.cdr.markForCheck();
    this.startCountdown();
    this.store.dispatch(ResendVerificationCode());
  }

  private startCountdown(): void {
    // Unsubscribe from previous countdown if it exists
    if (this.countDown) {
      this.countDown.unsubscribe();
    }
    this.countDown = interval(this.tick)
      .pipe(
        takeWhile(() => this.counter > 0),
        map(() => {
          this.counter -= this.tick;
          this.cdr.markForCheck();
          return this.counter;
        })
      )
      .subscribe(() => {});
  }
}
